/**
 * AthleteMediaManager — V13.4
 *
 * Gestion ultra-simple des 4 emplacements photo AITO.PF :
 *   - profile   (photo de profil)
 *   - banner    (bannière large)
 *   - gallery1  (photo complémentaire 1)
 *   - gallery2  (photo complémentaire 2)
 *
 * Chaque tuile : aperçu + Ajouter/Remplacer/Supprimer + toasts clairs.
 * Compteur global "X photos sur 4 utilisées".
 *
 * Le composant :
 *   - envoie un multipart POST /api/athletes/{id}/media/{slot}
 *   - envoie un DELETE /api/athletes/{id}/media/{slot}
 *   - notifie le parent via `onChange(updatedAthlete)` après chaque succès.
 *
 * Ne PATCH pas `photo_url` : la résolution canonique (media_files > photo_url >
 * fallback) est faite par `resolveAthletePhoto()`. Les champs URL restent
 * disponibles côté formulaire principal en repli.
 */
import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, Loader2, Trash2, ImageIcon, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { resolveAthletePhoto, resolveAthleteBanner } from "@/lib/photoFallback";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const SLOTS = [
  { id: "profile", label: "Photo de profil", hint: "1200×1200 conseillé — carré ou proche", aspect: "aspect-square" },
  { id: "banner", label: "Bannière personnelle", hint: "Format panoramique (ex : 1500×500)", aspect: "aspect-[3/1]" },
  { id: "gallery1", label: "Photo complémentaire 1", hint: "Portrait, action, événement…", aspect: "aspect-square" },
  { id: "gallery2", label: "Photo complémentaire 2", hint: "Portrait, action, événement…", aspect: "aspect-square" },
];

function fileFromSlot(files, slotId) {
  if (!Array.isArray(files)) return null;
  return files.find((f) => f?.slot === slotId) || null;
}

function slotDisplayUrl(athlete, slotId) {
  const f = fileFromSlot(athlete?.media_files, slotId);
  if (f?.public_url) {
    // public_url est relatif (/api/athlete-media/xxx.jpg) → on préfixe pour le rendu
    return f.public_url.startsWith("http") ? f.public_url : `${BACKEND}${f.public_url}`;
  }
  if (slotId === "profile") return resolveAthletePhoto(athlete);
  if (slotId === "banner") return resolveAthleteBanner(athlete);
  return null;
}

export default function AthleteMediaManager({ athlete, onChange, disabled = false }) {
  const fileInputs = useRef({});
  const [busy, setBusy] = useState({}); // { profile: true, banner: false, ... }
  const used = (athlete?.media_files || []).filter((f) => f?.slot && f?.public_url).length;

  const openPicker = (slotId) => fileInputs.current[slotId]?.click();

  const doUpload = async (slotId, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Fichier trop lourd (8 Mo maximum).");
      return;
    }
    setBusy((b) => ({ ...b, [slotId]: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(`/athletes/${athlete.id}/media/${slotId}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Photo enregistrée · ${res.data.used}/4 utilisées`);
      // Optimistic update : merge nouvelle ref dans athlete
      const others = (athlete.media_files || []).filter((f) => f?.slot !== slotId);
      onChange?.({
        ...athlete,
        media_files: [...others, res.data.media],
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'enregistrer la photo.");
    } finally {
      setBusy((b) => ({ ...b, [slotId]: false }));
    }
  };

  const doDelete = async (slotId) => {
    if (!confirm("Supprimer cette photo ?")) return;
    setBusy((b) => ({ ...b, [slotId]: true }));
    try {
      const res = await api.delete(`/athletes/${athlete.id}/media/${slotId}`);
      if (res.data.removed) toast.success("Photo supprimée.");
      const others = (athlete.media_files || []).filter((f) => f?.slot !== slotId);
      onChange?.({ ...athlete, media_files: others });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible.");
    } finally {
      setBusy((b) => ({ ...b, [slotId]: false }));
    }
  };

  const counter = useMemo(() => `${used} photo${used > 1 ? "s" : ""} sur 4 utilisée${used > 1 ? "s" : ""}`, [used]);

  return (
    <div className="space-y-4" data-testid="athlete-media-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide">Mes photos</h3>
          <p className="text-xs text-muted-foreground">
            4 emplacements maximum stockés directement dans AITO.PF. Les autres images (articles, résultats, médias)
            restent gérées par URL dans leurs modules dédiés.
          </p>
        </div>
        <div
          className="text-xs font-medium px-2.5 py-1 rounded-full bg-[hsl(var(--aito-lagoon))]/10 text-[hsl(var(--aito-lagoon))]"
          data-testid="athlete-media-counter"
        >
          {counter}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {SLOTS.map((slot) => {
          const url = slotDisplayUrl(athlete, slot.id);
          const hasUploaded = !!fileFromSlot(athlete?.media_files, slot.id);
          const isBusy = !!busy[slot.id];
          return (
            <div
              key={slot.id}
              className="rounded-xl border bg-card p-3 flex flex-col gap-2"
              data-testid={`slot-${slot.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold">{slot.label}</div>
                {hasUploaded ? (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700">
                    Upload AITO
                  </span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                    URL/fallback
                  </span>
                )}
              </div>
              <div
                className={`w-full ${slot.aspect} rounded-lg overflow-hidden border bg-muted/40 flex items-center justify-center`}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (disabled || isBusy) return;
                  const f = e.dataTransfer?.files?.[0];
                  if (f) doUpload(slot.id, f);
                }}
              >
                {url ? (
                  <img
                    src={url}
                    alt={slot.label}
                    className={`w-full h-full object-cover object-top`}
                  />
                ) : (
                  <ImageIcon className="h-8 w-8 text-muted-foreground/50" />
                )}
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug flex items-start gap-1">
                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                <span>{slot.hint}</span>
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={disabled || isBusy}
                  onClick={() => openPicker(slot.id)}
                  className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon))]/90 text-white"
                  data-testid={`slot-${slot.id}-upload`}
                >
                  {isBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  ) : (
                    <Camera className="h-4 w-4 mr-1.5" />
                  )}
                  {hasUploaded ? "Remplacer" : "Ajouter"}
                </Button>
                {hasUploaded && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={disabled || isBusy}
                    onClick={() => doDelete(slot.id)}
                    data-testid={`slot-${slot.id}-delete`}
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" />
                    Supprimer
                  </Button>
                )}
              </div>
              <input
                ref={(el) => (fileInputs.current[slot.id] = el)}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) doUpload(slot.id, f);
                }}
                data-testid={`slot-${slot.id}-input`}
              />
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-muted-foreground">
        Formats acceptés : JPG, PNG, WEBP · 8 Mo maximum par fichier (redimensionnement automatique).
        L'image uploadée est <strong>prioritaire</strong> sur une URL externe et sera utilisée partout (site, annuaire, cartes, PDF, partages).
      </p>
    </div>
  );
}
